import { Component, EventEmitter, Injector, Input, OnChanges, Output, ViewChild } from "@angular/core";
import { FoundationCsvImporterComponent } from "@app/main/modules/foundation/_components/foundation-csv-importer/foundation-csv-importer.component";
import { NavigationService } from "@app/services/navigation.service";
import { EngineerJobItemStatus, ImportStrategyReference } from "@foundation/foundation.enums";
import { EngineerJobStatus, IEngineerJob, IEngineerJobBookingWithEngineerMappings, IEngineerJobItem } from "@foundation/foundation.interfaces";
import { JobEditorComponent } from "@foundation/_routes/jobs/job-editor/job-editor.component";
import { AccountService } from "@foundation/_services/account.service";
import { EngineerJobBookingService } from "@foundation/_services/engineer-job-booking.service";
import { EngineerJobItemService } from "@foundation/_services/engineer-job-item.service";
import { EngineerJobService } from "@foundation/_services/engineer-job.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Subscription } from "rxjs";
import { FoundationNavigationModule } from "../../../foundation.navigation";
import './job-details.component.scss';

@UntilDestroy()
@Component({
    selector: "job-details",
    template: require("./job-details.component.html")
})
export class JobDetailsComponent implements OnChanges {
    @Input() jobId: number;
    @Output() newJobItem = new EventEmitter<void>();

    //--

    loading: boolean;

    job: IEngineerJob;
    jobBooking: IEngineerJobBookingWithEngineerMappings;

    jobItems: IEngineerJobItem[];

    jobItemsLength: number = 0;
    jobItemsCompleted: number = 0;

    engineerJobStatus = EngineerJobStatus;

    selectedTabIndex: number;

    jobIsClosed: boolean = false;
    saving: boolean = false;

    showImportSideBar: boolean = false;
    importStrategyRef: ImportStrategyReference = ImportStrategyReference.JobItem;
    @ViewChild('jobEditor') jobEditor: JobEditorComponent;
    @ViewChild("foundationCsvImporter") foundationCsvImporter: FoundationCsvImporterComponent;

    dataSubscription: Subscription;

    constructor(
        private readonly injector: Injector,
        private readonly accountService: AccountService,
        private readonly engineerJobBookingService: EngineerJobBookingService,
        private readonly engineerJobService: EngineerJobService,
        private readonly engineerJobItemService: EngineerJobItemService
    ) { };

    ngOnChanges(): void {
        this.jobId = Number(this.injector.get(NavigationService).getRouteParam('jobId'));

        this.loadJob();
    }

    addJobItem(): void {
        this.newJobItem.emit();
    }

    saveJob(): void {
        this.saving = true;

        if (!this.jobEditor) {
            this.saving = false;
            return;
        }

        this.jobEditor.saveJob();
    }

    jobSaved(job: IEngineerJob): void {
        if (job.Status === EngineerJobStatus.Cancelled || job.Status === EngineerJobStatus.Completed) {
            this.injector.get(NavigationService).go(FoundationNavigationModule.Jobs, null, false);
            return;
        }
        this.job = job;
        this.saving = false;
    }

    jobBookingSaved(jobBooking: IEngineerJobBookingWithEngineerMappings): void {
        this.jobBooking = jobBooking;
        this.job.jobBooking = jobBooking;
        this.engineerJobBookingService.setSelectedJobBooking(this.jobBooking);
    }

    cancelChanges(): void {
        this.jobEditor?.reset();
    }

    closeJob(cancel: boolean, complete = false): void {
        if (!cancel || !this.jobEditor) {
            return;
        }

        this.jobEditor.amendedJob.Status = complete ? EngineerJobStatus.Completed : EngineerJobStatus.Cancelled;
        this.cancelJobItems();
        this.saveJob();
    }

    onLastJobItemCancelled(): void {
        this.closeJob(true, true);
    }

    toggleJobItemImport(): void {
        this.showImportSideBar = !this.showImportSideBar
    }

    //--

    private loadJob(): void {
        this.loading = true;

        if (this.dataSubscription) {
            this.dataSubscription.unsubscribe();
        }

        this.dataSubscription = new Subscription();

        this.dataSubscription.add(combineLatest([
             this.engineerJobService.getEngineerJob(this.jobId),
             this.engineerJobBookingService.getSelectedJobBooking(this.jobId),
             this.engineerJobItemService.getEngineerJobItems({ JobId: this.jobId }),
             this.accountService.getCustomerAccounts()
        ]).pipe(untilDestroyed(this)).subscribe(([job, booking, jobItems, accounts]) => {
            this.job = job;
            this.jobBooking = booking;
            this.job.account = accounts.find(acc => acc.Id === this.job.AccountId);
            this.jobItems = jobItems;

            this.checkJobStatus();
            this.checkJobItemCompletion();
            this.loading = false;
        }))

        this.dataSubscription.add(this.engineerJobBookingService.selectedJobBookingUpdated.pipe(untilDestroyed(this)).subscribe(booking => {
            this.jobBooking = { ...booking };
        }));
    }

    private checkJobStatus(): void {
        this.jobIsClosed = false;

        if (this.job.Status !== EngineerJobStatus.Open) {
            this.jobIsClosed = true;
        }

    }

    private checkJobItemCompletion(): void {
        this.jobItemsCompleted = this.jobItemsLength = 0;

        if (!this.jobItems?.length) {
            return;
        }

        this.jobItemsCompleted = [...this.jobItems].filter(jobItem => jobItem.Status === EngineerJobItemStatus.Complete).length;
        this.jobItemsLength = [...this.jobItems].filter(jobItem => jobItem.Status !== EngineerJobItemStatus.Failed && jobItem.Status !== EngineerJobItemStatus.Cancelled).length;
    }

    private cancelJobItems(): void {
        this.jobItems?.forEach(jobItem => {
            this.engineerJobItemService.closeEngineerJobItem(jobItem).subscribe();
        })
    }
}
